/**
 * Customer Hooks
 *
 * Provides hooks for customer CRUD operations using createResourceHooks.
 * Includes additional custom hooks for search, etc.
 */

import { useQuery } from '@tanstack/react-query'
import { createResourceHooks } from './createResourceHooks'
import { raClient, type RAListParams } from '@/lib/raClient'
import { createQueryKeys } from '@/lib/queryKeys'
import type {
  Customer,
  CustomerListFilters,
  CustomerCreateInput,
  CustomerUpdateInput,
} from '@/types/customer'

// ============================================================================
// Query Keys
// ============================================================================

export const customerKeys = createQueryKeys('customers')

// ============================================================================
// Base CRUD Hooks (via createResourceHooks)
// ============================================================================

const customerHooks = createResourceHooks<Customer>('customers', customerKeys)

// Re-export with more descriptive names
export const useCustomerList = (
  params?: RAListParams & { filters?: CustomerListFilters },
  options?: { enabled?: boolean; staleTime?: number }
) => {
  // Build filter from CustomerListFilters
  const filter: Record<string, unknown> = { ...params?.filter }
  if (params?.filters?.name) {
    filter.name = `%${params.filters.name}%`
  }
  if (params?.filters?.q) {
    filter.q = params.filters.q
  }

  return customerHooks.useList(
    {
      page: params?.page,
      perPage: params?.perPage,
      sort: params?.sort ?? { field: 'id', order: 'DESC' },
      filter,
    },
    options
  )
}

export const useCustomer = customerHooks.useOne

export const useCreateCustomer = () => {
  const mutation = customerHooks.useCreate()
  return {
    ...mutation,
    mutateAsync: (data: CustomerCreateInput) =>
      mutation.mutateAsync(data as Partial<Customer>),
    mutate: (data: CustomerCreateInput) => mutation.mutate(data as Partial<Customer>),
  }
}

export const useUpdateCustomer = () => {
  const mutation = customerHooks.useUpdate()
  return {
    ...mutation,
    mutateAsync: (data: CustomerUpdateInput) =>
      mutation.mutateAsync({ id: data.id, data }),
    mutate: (data: CustomerUpdateInput) => mutation.mutate({ id: data.id, data }),
  }
}

export const useDeleteCustomer = customerHooks.useDelete

// ============================================================================
// Custom Hooks
// ============================================================================

/**
 * Hook to get simple list of all customers (for dropdowns, etc.)
 * Fetches all customers without pagination
 */
export function useCustomersSimple(enabled = true) {
  return useQuery({
    queryKey: [...customerKeys.all, 'simple'],
    queryFn: async () => {
      const result = await raClient.getList<Customer>('customers', {
        page: 1,
        perPage: 999,
        sort: { field: 'name', order: 'ASC' },
      })
      return result.data
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Alias for useCustomersSimple for backward compatibility
 */
export const useCustomers = useCustomersSimple

/**
 * Hook to search customers with real-time autocomplete
 */
export function useSearchCustomers(searchTerm: string, enabled = true) {
  return useQuery({
    queryKey: [...customerKeys.all, 'search', searchTerm],
    queryFn: async () => {
      const result = await raClient.getList<Customer>('customers', {
        page: 1,
        perPage: 50,
        sort: { field: 'name', order: 'ASC' },
        filter: searchTerm ? { name: `%${searchTerm}%` } : {},
      })
      return result.data
    },
    enabled,
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
  })
}

/**
 * Hook to get specific customers by IDs
 */
export function useCustomersByIds(ids: number[], enabled = true) {
  return useQuery({
    queryKey: customerKeys.byIds(ids),
    queryFn: () => raClient.getMany<Customer>('customers', ids),
    enabled: enabled && ids.length > 0,
    staleTime: 5 * 60 * 1000,
  })
}
